/* eslint-disable import/prefer-default-export */
import axios from "axios";
import gql from "graphql-tag";
import getMaintenanceMode, { getFingerprint } from "../../utilities";

const graphql = require("graphql");

const { print } = graphql;

interface SessionDetails {
  correlationId: string;
  brand: string;
  country: string;
  source: string;
}

// When the accounts are linked and go directly to the MFA flow from the linking status page, 
// loginAuthenticatorCode and username will not be passed as parameters to the AppSync query.
interface Payload {
  token: string;
  username?: string;
  loginAuthenticatorCode?: string;
  sessionDetails: SessionDetails;
}

type VerifyOutcome = "skipMfa" | "requireMfa" | "error";

type VerifyReasonCode =
  | "missing_dashboard_token"
  | "missing_default_account"
  | "missing_mfa_destination"
  | "missing_required_identity"
  | "login_authenticator_not_verified"
  | "api_errors"
  | "unexpected_response";

const gqlRequest = gql`
  query CustomerVerifyRememberMe($Request: VerifyCustomerRememberMeRequest!) {
    verifyCustomerRememberMe(Request: $Request) {
      IsDeviceRemembered
      IsLoginAuthenticatorCodeVerified
      LoginAuthenticatorCode
      DefaultAccountNumber
      Token {
        AccessToken
        ExpiresIn
        TokenType
        RefreshToken
        IdToken
      }
      PartyId
      Email
      PhoneNumber
      Errors {
        ErrorCode
        ErrorMessage
        ErrorStack
      } 
    }
  }
`;

export const verifyCustomerRememberMe = async (payload: Payload) => {
  const { token, username, loginAuthenticatorCode, sessionDetails } = payload;
  try {
    const userFingerprint = await getFingerprint();
    const getMode = await getMaintenanceMode();
    // Short-circuit the entire flow if the app is in maintenance mode.
    if (getMode === true) {
      return {
        data: {
          inMaintenance: "Y",
        },
      };
    }

    const request = {
      Username: username,
      Fingerprint: `${userFingerprint}`,
      Country: sessionDetails.country,
      Brand: sessionDetails.brand,
      CorrelationId: sessionDetails.correlationId,
      Source: sessionDetails.source,
      LoginAuthenticatorCode: loginAuthenticatorCode,
      ParentFunctionName: "VerifyCustomerRememberMe",
      ProcessName: "VerifyCustomerRememberMe",
      SourceApp: sessionDetails.source,
      WidgetName: sessionDetails.source,
    };

    const appsyncUrl = process.env.REACT_APP_APPSYNC_URL ?? '';
    const url = `${appsyncUrl}/customerauth/authenticated/graphql`;

    const response = await axios({
      url,
      method: "post",
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      data: {
        query: print(gqlRequest),
        variables: {
          Request: request,
        },
      },
    });

    const verifyResponse = response?.data?.data?.verifyCustomerRememberMe;

    // Guard: the GraphQL response shape was completely absent — API or network anomaly.
    if (!verifyResponse) {
      return {
        data: {
          isSuccess: false,
          outcome: "error" as VerifyOutcome,
          reasonCode: "unexpected_response" as VerifyReasonCode,
          error: "Unable to verify account status. Please try again.",
        },
      };
    }

    // Guard: the API returned explicit error objects or a null data payload.
    if (verifyResponse?.Errors || response?.data?.data === null) {
      return {
        data: {
          isSuccess: false,
          outcome: "error" as VerifyOutcome,
          reasonCode: "api_errors" as VerifyReasonCode,
          errors: verifyResponse?.Errors?.[0] || response?.data?.errors || "",
        },
      };
    }
    
    const isDeviceRemembered = verifyResponse?.IsDeviceRemembered === true;
    const isLoginAuthenticatorCodeVerified = verifyResponse?.IsLoginAuthenticatorCodeVerified === true;
    const isLinkedFlow = !username;
    const hasReturnedLoginAuthenticatorCode = Boolean(verifyResponse?.LoginAuthenticatorCode);
    const verifiedFlagAbsent =
      verifyResponse?.IsLoginAuthenticatorCodeVerified === null ||
      verifyResponse?.IsLoginAuthenticatorCodeVerified === undefined;
    const allowBootstrapWithoutVerifiedFlag =
      isLinkedFlow &&
      verifiedFlagAbsent &&
      hasReturnedLoginAuthenticatorCode;
    // Guard: the login session has not been authenticated by the API.
    // Linked-user flows (identified by the absence of username) are exempt when
    // the API omits the verified flag but still returns a LoginAuthenticatorCode.
    // The usual verifyRememberMe API returns IsLoginAuthenticatorCodeVerified as
    // null when the user is not verified, or undefined when the linked flow does
    // not pass username/loginAuthenticatorCode.  In both linked cases the API
    // returns a LoginAuthenticatorCode, so we allow the flow to continue.
    if (!isDeviceRemembered && !isLoginAuthenticatorCodeVerified && !allowBootstrapWithoutVerifiedFlag) {
      return {
        data: {
          isSuccess: false,
          outcome: "error" as VerifyOutcome,
          reasonCode: "login_authenticator_not_verified" as VerifyReasonCode,
          isLoginAuthenticatorCodeVerified: false,
          isDeviceRemembered: Boolean(verifyResponse?.IsDeviceRemembered),
          errors: {
            ErrorCode: "login_authenticator_not_verified",
            ErrorMessage: "Unable to verify account status. Please try again.",
          },
        },
      };
    }

    const dashboardToken = verifyResponse?.Token?.IdToken || "";
    const accountNumber = verifyResponse?.DefaultAccountNumber || "";
    const email = verifyResponse?.Email || "";
    const phoneNumber = verifyResponse?.PhoneNumber || "";
    const partyId = verifyResponse?.PartyId;
    const hasPartyId = partyId !== undefined && partyId !== null;
    const hasEmail = Boolean(email);
    const hasChallengeDestination = Boolean(email || phoneNumber);

    // Guard: email and partyId are required to proceed to MFA when isDeviceRemembered false.
    // Their absence means the API could not identify the customer.
    if (!isDeviceRemembered && (!hasEmail || !hasPartyId)) {
      return {
        data: {
          isSuccess: false,
          outcome: "error" as VerifyOutcome,
          reasonCode: "missing_required_identity" as VerifyReasonCode,
          isLoginAuthenticatorCodeVerified: true,
          isDeviceRemembered,
          loginAuthenticatorCode: verifyResponse?.LoginAuthenticatorCode,
          errors: {
            ErrorCode: "missing_required_identity",
            ErrorMessage: "Unable to verify account status. Please try again.",
          },
        },
      };
    }

    // Happy path: device is trusted, and we have a full session — MFA can be skipped.
    if (isDeviceRemembered && dashboardToken && accountNumber) {
      return {
        data: {
          isSuccess: true,
          outcome: "skipMfa" as VerifyOutcome,
          isLoginAuthenticatorCodeVerified: true,
          isDeviceRemembered: true,
          loginAuthenticatorCode: verifyResponse?.LoginAuthenticatorCode,
          token: dashboardToken,
          accountNumber,
          partyId,
          email,
          phoneNumber,
        },
      };
    }

    // Guard: MFA requires at least one delivery channel (email or phone).
    // Without one, there is nowhere to send the verification code.
    if (!hasChallengeDestination) {
      return {
        data: {
          isSuccess: false,
          outcome: "error" as VerifyOutcome,
          reasonCode: "missing_mfa_destination" as VerifyReasonCode,
          isLoginAuthenticatorCodeVerified: true,
          isDeviceRemembered,
          loginAuthenticatorCode: verifyResponse?.LoginAuthenticatorCode,
          errors: {
            ErrorCode: "missing_mfa_destination",
            ErrorMessage: "Unable to verify account status. Please try again.",
          },
        },
      };
    }

    // Fallthrough: all guards passed — customer must complete MFA.
    // If the device was remembered but the session is incomplete, attach a reasonCode
    // so the caller knows which field is missing (token vs account number).
    let reasonCode: VerifyReasonCode | undefined;
    if (isDeviceRemembered) {
      reasonCode = dashboardToken
        ? "missing_default_account"
        : "missing_dashboard_token";
    }

    return {
      data: {
        isSuccess: true,
        outcome: "requireMfa" as VerifyOutcome,
        ...(reasonCode ? { reasonCode } : {}),
        isLoginAuthenticatorCodeVerified: true,
        isDeviceRemembered: false,
        loginAuthenticatorCode: verifyResponse?.LoginAuthenticatorCode,
        token: dashboardToken,
        accountNumber,
        partyId,
        email,
        phoneNumber,
      },
    };
  } catch (error) {
    const normalizedError = error as Error;
    return { error: normalizedError.message || normalizedError.toString() };
  }
};
